import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { IMG } from "@/lib/assets";
import { startVoice } from "@/lib/realtimeVoice";
import { CoachAvatar } from "@/components/ui/CoachAvatar";
import { ResponsiveContainer, Line, XAxis, YAxis, Tooltip, Bar, ComposedChart } from "recharts";
import { Mic, Square, HeartHandshake, Phone, Clock, Loader2, Info } from "lucide-react";

const SCALES = [
  { key: "mood", label: "Mood", faces: ["😞", "🙁", "😐", "🙂", "😄"], c: "#CCFF00" },
  { key: "stress", label: "Stress", faces: ["😌", "🙂", "😐", "😣", "😫"], c: "#FF6B6B" },
  { key: "sleep", label: "Sleep last night", faces: ["😵", "🥱", "😐", "😴", "🌙"], c: "#7C9CFF" },
  { key: "energy", label: "Energy", faces: ["🪫", "😪", "😐", "⚡", "🔥"], c: "#FFB340" },
];

export function CalmRecovery() {
  const qc = useQueryClient();
  const { data: sessions = [] } = useQuery({ queryKey: ["calm-sessions"], queryFn: async () => (await api.get("/api/calm/sessions")).data });
  const { data: wb } = useQuery({ queryKey: ["wellbeing"], queryFn: async () => (await api.get("/api/wellbeing", { params: { days: 30 } })).data });
  const [vals, setVals] = useState({ mood: 3, stress: 3, sleep: 3, energy: 3 });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [support, setSupport] = useState(null);
  const [live, setLive] = useState(null);   // session id
  const [status, setStatus] = useState("idle");
  const voiceRef = useRef(null);

  useEffect(() => () => { voiceRef.current?.stop?.(); }, []);

  const checkin = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/api/wellbeing/checkin", { ...vals, note });
      if (data.crisis) setSupport(data.support); else toast.success("Thanks for checking in. Your coach will factor this into your training.");
      setNote(""); qc.invalidateQueries({ queryKey: ["wellbeing"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggle = async (s) => {
    if (voiceRef.current) { voiceRef.current.stop(); voiceRef.current = null; setLive(null); setStatus("idle"); if (live === s.id) return; }
    try {
      setLive(s.id);
      voiceRef.current = await startVoice({ topic: `calm:${s.id}`, onStatus: setStatus, onEvent: () => {} });
    } catch (e) {
      setLive(null); setStatus("idle");
      toast.error(e?.name === "NotAllowedError" ? "Please allow microphone access to start a voice session." : apiErr(e, "Couldn't start the voice session."));
    }
  };

  const series = (wb?.series || []).map((d) => ({ ...d, label: new Date(d.day).toLocaleDateString(undefined, { day: "numeric", month: "short" }) }));
  const tip = { contentStyle: { background: "#0F1115", border: "1px solid #1E222A", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#8A91A6" } };

  return (
    <section className="space-y-5" data-testid="calm-recovery">
      <div className="relative overflow-hidden rounded-2xl border border-[#1E222A] min-h-[14rem] flex items-end">
        <img src={IMG.meditation} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#050505 10%,rgba(5,5,5,0.55) 60%,rgba(5,5,5,0.15))" }} />
        <div className="relative p-6 md:p-8 max-w-2xl">
          <span className="mono-label text-acid">calm & recovery</span>
          <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-1">Train your mind as well as your body.</h2>
          <p className="text-[#B7BECB] mt-2 text-sm">Guided voice sessions for stress, sleep and recovery with your OCTON coach, plus a 20-second daily check-in that shapes your training.</p>
          <p className="text-[#8A91A6] mt-3 text-xs flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />Wellbeing support, not medical or psychological treatment. For ongoing anxiety or low mood, please speak to your GP or a qualified therapist.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Check-in */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-5 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5" data-testid="wellbeing-checkin">
          <div className="flex items-center justify-between">
            <p className="font-heading font-bold text-lg">Daily check-in</p>
            {wb?.checked_in_today && <span className="mono-label text-acid">done today ✓</span>}
          </div>
          <div className="space-y-3 mt-3">
            {SCALES.map((sc) => (
              <div key={sc.key}>
                <p className="mono-label mb-1">{sc.label}</p>
                <div className="flex gap-1.5">
                  {sc.faces.map((f, i) => {
                    const on = vals[sc.key] === i + 1;
                    return (
                      <button key={i} type="button" onClick={() => setVals({ ...vals, [sc.key]: i + 1 })}
                        aria-label={`${sc.label} ${i + 1} of 5`}
                        className="flex-1 h-11 rounded-lg text-xl border transition-all"
                        style={on ? { borderColor: sc.c, background: `${sc.c}22`, transform: "scale(1.06)" } : { borderColor: "#1E222A" }}>{f}</button>
                    );
                  })}
                </div>
              </div>
            ))}
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything on your mind? (optional)"
              className="w-full h-11 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" />
            <button onClick={checkin} disabled={saving} className="w-full h-11 rounded-lg bg-acid text-black font-medium hover:bg-[#AACC00]" data-testid="checkin-save">
              {saving ? "Saving…" : "Save check-in"}
            </button>
          </div>
        </motion.div>

        {/* Trend */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-7 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5">
          <div className="flex items-start gap-3">
            <CoachAvatar size={40} />
            <div>
              <p className="font-heading font-bold text-lg">Mood, stress and training</p>
              <p className="text-sm text-[#B7BECB]">{wb?.tip || "Check in for a few days and your coach will spot patterns between how you feel and how you train."}</p>
            </div>
          </div>
          <div className="h-52 mt-3">
            {series.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series}>
                  <XAxis dataKey="label" tick={{ fill: "#565C6F", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="s" domain={[1, 5]} hide />
                  <YAxis yAxisId="t" orientation="right" hide />
                  <Tooltip {...tip} />
                  <Bar yAxisId="t" dataKey="training_min" name="Training (min)" fill="#3D7BFF33" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="s" type="monotone" dataKey="mood" name="Mood" stroke="#CCFF00" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="s" type="monotone" dataKey="stress" name="Stress" stroke="#FF6B6B" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="s" type="monotone" dataKey="sleep" name="Sleep" stroke="#7C9CFF" strokeWidth={2} dot={false} strokeDasharray="4 3" />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full rounded-lg border border-dashed border-[#1E222A] flex items-center justify-center text-sm text-[#565C6F]">Your chart appears after your first check-in.</div>
            )}
          </div>
          <div className="flex flex-wrap gap-4 mt-2">
            {[["Mood", "#CCFF00"], ["Stress", "#FF6B6B"], ["Sleep", "#7C9CFF"], ["Training", "#3D7BFF"]].map(([l, c]) => (
              <span key={l} className="mono-label flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />{l}</span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Voice sessions */}
      <div>
        <p className="font-heading font-bold text-xl mb-3">Guided voice sessions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {sessions.map((s, i) => {
            const on = live === s.id;
            return (
              <motion.div key={s.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className={`relative rounded-xl overflow-hidden border ${on ? "border-acid shadow-[0_0_30px_rgba(204,255,0,0.25)]" : "border-[#1E222A]"} bg-[#0F1115]`} data-testid="calm-session">
                <div className="relative h-28 overflow-hidden">
                  <img src={IMG[s.image] || IMG.meditation} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#0F1115,rgba(15,17,21,0.1))" }} />
                  <span className="absolute top-2 right-2 mono-label bg-black/60 rounded-full px-2 py-0.5 flex items-center gap-1"><Clock className="w-3 h-3" />{s.minutes} min</span>
                </div>
                <div className="p-4 pt-1">
                  <p className="font-heading font-bold">{s.title}</p>
                  <p className="text-xs text-[#8A91A6] mt-0.5">{s.focus}</p>
                  <button onClick={() => toggle(s)} className={`mt-3 w-full h-10 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 ${on ? "bg-white/10 text-white" : "bg-acid text-black hover:bg-[#AACC00]"}`}>
                    {on ? (status === "connecting" ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</> : <><Square className="w-4 h-4" /> End session</>) : <><Mic className="w-4 h-4" /> Start voice session</>}
                  </button>
                </div>
                <AnimatePresence>
                  {on && status === "live" && (
                    <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="absolute top-2 left-2 mono-label bg-acid text-black rounded-full px-2 py-0.5 animate-pulse">live</motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Support */}
      <div className="rounded-xl border border-[#7C9CFF]/30 bg-[#0D0F16] p-5 flex flex-col md:flex-row gap-4 md:items-center" data-testid="support-card">
        <HeartHandshake className="w-8 h-8 text-[#7C9CFF] shrink-0" />
        <div className="flex-1 text-sm text-[#B7BECB]">
          <p className="font-heading font-bold text-white text-base">Need to talk to someone now?</p>
          Emergency: <b className="text-white">112</b> (EU) or <b className="text-white">999</b> (UK). Samaritans (UK, free, 24/7): <b className="text-white">116 123</b>. Portugal: SNS 24 <b className="text-white">808 24 24 24</b> (choose psychological support).
        </div>
        <a href="tel:116123" className="inline-flex items-center gap-2 rounded-full border border-[#7C9CFF]/60 text-[#B9C8FF] px-4 h-10 text-sm hover:bg-[#7C9CFF]/10"><Phone className="w-4 h-4" /> Call Samaritans</a>
      </div>

      {support && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="max-w-md rounded-2xl border border-[#7C9CFF]/40 bg-[#0F1115] p-6">
            <HeartHandshake className="w-8 h-8 text-[#7C9CFF]" />
            <p className="font-heading font-black text-xl mt-3">You don't have to handle this alone</p>
            <p className="text-sm text-[#B7BECB] mt-2">{support}</p>
            <div className="flex gap-2 mt-5">
              <a href="tel:116123" className="flex-1 h-11 rounded-full bg-[#7C9CFF] text-black font-medium inline-flex items-center justify-center gap-2"><Phone className="w-4 h-4" /> 116 123</a>
              <button onClick={() => setSupport(null)} className="flex-1 h-11 rounded-full border border-[#343A4A]">Close</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
